import React, { useState } from "react";
import {Create} from "@refinedev/mui";
import {Box, TextField, Alert, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel, Typography} from "@mui/material";
import {useForm} from "@refinedev/react-hook-form";
import {IResourceComponentsProps, useTranslate, useNotification, useNavigation} from "@refinedev/core";
import { useAuth } from "../../contexts/auth-context";
import axiosInstance from "../../api/axios-config";

export const CompanyCreate: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const { user: currentUser } = useAuth();
    const { open } = useNotification();
    const { list } = useNavigation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orgType, setOrgType] = useState<'family' | 'enterprise'>('family');

    const isGuest = currentUser?.role === 'GUEST';

    const {
        saveButtonProps,
        refineCore: { formLoading },
        register,
        formState: { errors },
        handleSubmit,
    } = useForm();

    const onSubmit = async (data: any) => {
        setIsSubmitting(true);

        try {
            // Pour les GUEST, utiliser l'endpoint spécial qui crée et assigne
            if (isGuest) {
                const response = await axiosInstance.post('/company/create-and-assign', {
                    nom: data.nom,
                    limitUser: data.limitUser || (orgType === 'family' ? 10 : 1000)
                });

                console.log('Company created and user assigned:', response.data);

                open?.({
                    type: "success",
                    message: translate('company.create_success', 'Organisation créée avec succès !'),
                    description: translate('company.role_updated', 'Votre rôle a été mis à jour en Modérateur.'),
                });

                // Rafraîchir la page après un court délai pour recharger avec les nouveaux droits
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                // Pour les autres rôles, utiliser la création standard
                const response = await axiosInstance.post('/company', {
                    nom: data.nom,
                    limitUser: data.limitUser
                });

                open?.({
                    type: "success",
                    message: translate('company.create_success', 'Organisation créée avec succès !'),
                });

                list('company');
            }
        } catch (error: any) {
            console.error('Error creating company:', error);
            open?.({
                type: "error",
                message: translate('company.create_error', 'Erreur lors de la création'),
                description: error.response?.data?.message || error.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Create
            isLoading={formLoading || isSubmitting}
            saveButtonProps={{
                ...saveButtonProps,
                onClick: handleSubmit(onSubmit),
                disabled: isSubmitting
            }}
            title={isGuest ? translate('company.create_your_org', 'Créez votre organisation') : translate('company.create', 'Créer une entreprise')}
        >
            <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                autoComplete="off"
            >
                {isGuest && (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate('company.guest_create_info', 'Une fois votre organisation créée, vous deviendrez automatiquement Modérateur et pourrez inviter des membres.')}
                        </Alert>

                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <FormLabel component="legend">
                                {translate('company.org_type', 'Type d\'organisation')}
                            </FormLabel>
                            <RadioGroup
                                value={orgType}
                                onChange={(e) => setOrgType(e.target.value as 'family' | 'enterprise')}
                            >
                                <FormControlLabel
                                    value="family"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                {translate('company.type_family', 'Famille')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('company.type_family_desc', 'Jusqu\'à 10 membres')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="enterprise"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                {translate('company.type_enterprise', 'Entreprise')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('company.type_enterprise_desc', 'Jusqu\'à 1000 membres')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}

                <TextField
                    {...register("nom", {
                        required: translate('validation.required', 'Ce champ est requis'),
                    })}
                    error={!!(errors as any)?.nom}
                    helperText={(errors as any)?.nom?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label={translate("company.fields.name")}
                    name="nom"
                    placeholder={
                        isGuest
                            ? (orgType === 'family'
                                ? translate('company.name_placeholder_family', 'Ex: Famille Dupont')
                                : translate('company.name_placeholder_enterprise', 'Ex: ACME Corp'))
                            : translate('company.name_placeholder', 'Nom de l\'entreprise')
                    }
                />

                {!isGuest && (
                    <TextField
                        {...register("limitUser", {
                            required: translate('validation.required', 'Ce champ est requis'),
                            min: {
                                value: 1,
                                message: translate('validation.min_users', 'Minimum 1 utilisateur')
                            }
                        })}
                        error={!!(errors as any)?.limitUser}
                        helperText={(errors as any)?.limitUser?.message}
                        margin="normal"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        type="number"
                        label={translate("company.fields.limit")}
                        name="limitUser"
                        defaultValue={100}
                    />
                )}
            </Box>
        </Create>
    );
};
